/**
 * Chip layer — replaces
 *   packages/amgbandarqq_v2/lib/presentation/flame/bandarqq_flame_game.dart.
 *
 * 9 bet areas, indexed by server bet-item ID (291..299). Flame's
 * `ChipPosition(minXPct, rangeXpx, minYPct, rangeYpx)` is preserved:
 *   endX = REF_WIDTH  * minXPct + random * rangeXpx
 *   endY = REF_HEIGHT * minYPct + random * rangeYpx
 *
 * Animations:
 *   Placement: 0.3s linear, from (REF_WIDTH/2, REF_HEIGHT+50).
 *   Win/Lose: 0.5s with 0.02s × chipIndex stagger, opacity fades to 0.2.
 *   Tie: half → dealer, half → player.
 *   After 600 ms post-trigger the chips are destroyed.
 */
import { Container } from 'pixi.js';
import type { BetItemId } from '../types/game';
import { ChipSprite, preloadCoinTextures } from './chipSprite';
import { Easings, tween } from './tween';
import { REF_HEIGHT, REF_WIDTH } from '../config';

interface XY {
  x: number;
  y: number;
}

interface ScatterRect {
  minX: number;
  rangeX: number;
  minY: number;
  rangeY: number;
}

/**
 * bandarqq_flame_game.dart:15-25 — ChipPosition(minXPct, rangeXpx, minYPct, rangeYpx).
 *   291: top-left  (player 1)
 *   293: top-mid-left (player 2)
 *   299: top-mid-right (player 3)
 *   292: top-right (player 4)
 *   294: bottom-left  (player 5 / quick-twin)
 *   297: bottom-mid-left  (player 6 / quick-twin)
 *   296: bottom-mid (player 7)
 *   298: bottom-mid-right (player 8 / quick-twin)
 *   295: bottom-right (player 9 / quick-twin)
 */
const SCATTER: Record<BetItemId, ScatterRect> = {
  291: { minX: 0.226 * REF_WIDTH, rangeX: 300, minY: 0.285 * REF_HEIGHT, rangeY: 280 },
  293: { minX: 0.43 * REF_WIDTH, rangeX: 260, minY: 0.34 * REF_HEIGHT, rangeY: 85 },
  299: { minX: 0.425 * REF_WIDTH, rangeX: 270, minY: 0.52 * REF_HEIGHT, rangeY: 85 },
  292: { minX: 0.61 * REF_WIDTH, rangeX: 300, minY: 0.285 * REF_HEIGHT, rangeY: 280 },
  294: { minX: 0.19 * REF_WIDTH, rangeX: 140, minY: 0.685 * REF_HEIGHT, rangeY: 85 },
  297: { minX: 0.3 * REF_WIDTH, rangeX: 150, minY: 0.685 * REF_HEIGHT, rangeY: 85 },
  296: { minX: 0.42 * REF_WIDTH, rangeX: 280, minY: 0.685 * REF_HEIGHT, rangeY: 85 },
  298: { minX: 0.614 * REF_WIDTH, rangeX: 150, minY: 0.685 * REF_HEIGHT, rangeY: 85 },
  295: { minX: 0.73 * REF_WIDTH, rangeX: 140, minY: 0.685 * REF_HEIGHT, rangeY: 85 },
};

const PLAYER_ORIGIN: XY = { x: REF_WIDTH / 2, y: REF_HEIGHT + 50 };
const DEALER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.2 };
const PLAYER_WIN_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 1.1 };

interface ActiveChip {
  betItemId: BetItemId;
  sprite: ChipSprite;
}

export class ChipLayer {
  private readonly root: Container;
  private active: ActiveChip[] = [];

  constructor(parent: Container) {
    this.root = new Container();
    this.root.label = 'ChipLayer';
    parent.addChild(this.root);
    void preloadCoinTextures();
  }

  placeChip(betItemId: BetItemId, chipValue: number, chipIndex: number): void {
    const rect = SCATTER[betItemId];
    if (!rect) return;

    const target: XY = {
      x: rect.minX + Math.random() * rect.rangeX,
      y: rect.minY + Math.random() * rect.rangeY,
    };

    const sprite = new ChipSprite(chipValue, chipIndex);
    sprite.position.set(PLAYER_ORIGIN.x, PLAYER_ORIGIN.y);
    this.root.addChild(sprite);

    const from = { ...PLAYER_ORIGIN };
    tween({
      durationMs: 300,
      easing: Easings.linear,
      onUpdate: (t) => {
        sprite.position.set(
          from.x + (target.x - from.x) * t,
          from.y + (target.y - from.y) * t,
        );
      },
    });

    this.active.push({ betItemId, sprite });
  }

  /** Winning chips fly to player; losing-area chips go to dealer. */
  animateWinningChips(winningBetIds: ReadonlySet<BetItemId>): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => {
      const target = winningBetIds.has(c.betItemId)
        ? PLAYER_WIN_TARGET
        : DEALER_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  animateLosingChips(): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => this.flyAndFade(c, DEALER_TARGET, i));
    this.scheduleDestroyAll(oldActive, 600);
  }

  animateTie(): void {
    const oldActive = this.active;
    this.active = [];
    const half = Math.ceil(oldActive.length / 2);
    oldActive.forEach((c, i) => {
      const target = i < half ? DEALER_TARGET : PLAYER_WIN_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  hardClear(): void {
    for (const c of this.active) c.sprite.destroy();
    this.active = [];
  }

  hasAnyChips(): boolean {
    return this.active.length > 0;
  }

  // -----------------------------------------------------------------

  private flyAndFade(c: ActiveChip, target: XY, index: number): void {
    const fromX = c.sprite.position.x;
    const fromY = c.sprite.position.y;
    const fromAlpha = c.sprite.alpha;
    const delayMs = 20 * index;

    window.setTimeout(() => {
      tween({
        durationMs: 500,
        easing: Easings.linear,
        onUpdate: (t) => {
          c.sprite.position.set(
            fromX + (target.x - fromX) * t,
            fromY + (target.y - fromY) * t,
          );
          c.sprite.alpha = fromAlpha + (0.2 - fromAlpha) * t;
        },
      });
    }, delayMs);
  }

  private scheduleDestroyAll(list: ActiveChip[], delayMs: number): void {
    window.setTimeout(() => {
      for (const c of list) {
        try {
          c.sprite.destroy();
        } catch {
          /* already destroyed */
        }
      }
    }, delayMs);
  }
}
